// The student-employment requirements a peer supporter attests to, shared by
// the eligibility step, the review summary and any other page that shows the
// requirement wording. The checks mirror
// server/src/services/employmentAttestation.js and studentEmploymentRules.js.

export const STUDENT_EMPLOYMENT_REQUIREMENTS_URL =
    'https://www.msudenver.edu/human-resources/student-employment/opportunities/requirements/';

// The seven answers. null means not answered.
export const EMPTY_EMPLOYMENT_ATTESTATION = Object.freeze({
    authorizedToWork: null,
    meetsMinimumAge: null,
    isInternational: null,
    studentLevel: null,
    meetsCreditHours: null,
    graduatingSemesterException: null,
    meetsSemesterGpa: null,
});

const ANSWER_KEYS = Object.keys(EMPTY_EMPLOYMENT_ATTESTATION);
const STUDENT_LEVELS = ['undergraduate', 'graduate'];

const isAnswered = (value) => value !== null && value !== undefined;

/**
 * A stored attestation as the seven answers: a missing one is null, and the
 * server's confirmedAt and rulesSnapshot are left out.
 */
export function employmentAttestationFrom(stored) {
    const answers = { ...EMPTY_EMPLOYMENT_ATTESTATION };
    for (const key of ANSWER_KEYS) {
        if (isAnswered(stored?.[key])) answers[key] = stored[key];
    }
    return answers;
}

/** True when every question the answers call for has an answer. */
export function isEmploymentAttestationComplete(answers) {
    return unansweredCount(answers) === 0;
}

/**
 * How many of the questions now shown have no answer. Credit hours counts
 * while it is hidden, since it is asked before the student can continue.
 */
export function unansweredCount(answers) {
    const a = answers || {};
    let count = 0;
    for (const key of [
        'authorizedToWork',
        'meetsMinimumAge',
        'isInternational',
        'meetsCreditHours',
        'meetsSemesterGpa',
    ]) {
        if (typeof a[key] !== 'boolean') count += 1;
    }
    if (
        a.isInternational === true &&
        !STUDENT_LEVELS.includes(a.studentLevel)
    ) {
        count += 1;
    }
    if (
        a.meetsCreditHours === false &&
        typeof a.graduatingSemesterException !== 'boolean'
    ) {
        count += 1;
    }
    return count;
}

/**
 * How many answers do not meet a requirement. An unanswered question does
 * not, and a graduate short on credit hours meets the enrollment requirement.
 */
export function unmetAnswerCount(answers) {
    const a = answers || {};
    return [
        a.authorizedToWork === false,
        a.meetsMinimumAge === false,
        a.meetsCreditHours === false && a.graduatingSemesterException !== true,
        a.meetsSemesterGpa === false,
    ].filter(Boolean).length;
}

/** The credit-hour minimum that applies, or null until it is known. */
export function creditHourMinimumFor(rules, answers) {
    if (!rules || !answers) return null;
    if (answers.isInternational === false) return rules.minimumCreditHours;
    if (answers.isInternational === true && answers.studentLevel) {
        return (
            rules.internationalMinimumCreditHours?.[answers.studentLevel] ??
            null
        );
    }
    return null;
}

/**
 * New answers with `field` set and every answer that depended on it cleared.
 * The same object when the value did not change.
 */
export function withAnswer(answers, field, value) {
    if (answers[field] === value) return answers;
    const next = { ...answers, [field]: value };
    if (field === 'isInternational') {
        next.studentLevel = null;
        next.meetsCreditHours = null;
        next.graduatingSemesterException = null;
    } else if (field === 'studentLevel') {
        next.meetsCreditHours = null;
        next.graduatingSemesterException = null;
    } else if (field === 'meetsCreditHours') {
        next.graduatingSemesterException = null;
    }
    return next;
}

/** 2 -> '2.0', 2.5 -> '2.5' */
export function formatGpa(value) {
    return Number.isInteger(value) ? value.toFixed(1) : String(value);
}

/** The five requirement sentences, with the numbers taken from the rules. */
export function studentEmploymentRequirementLines(rules) {
    const international = rules.internationalMinimumCreditHours;
    return [
        `At least ${rules.minimumAge} years old.`,
        `Enrolled in at least ${rules.minimumCreditHours} credit hours in fall and spring (the final semester before graduation is exempt).`,
        `International students: enrolled full time (${international.undergraduate} credit hours undergraduate, ${international.graduate} graduate), an Employment Eligibility Form each semester, and a Social Security number.`,
        `Semester GPA of at least ${formatGpa(rules.minimumSemesterGpa)} (one semester of probation allowed).`,
        'Graduates may work one semester after graduation in the same department.',
    ];
}

/** 'Campus jobs are limited to 30 hours a week combined (20 for ...).' */
export function weeklyHoursLimitLine(rules) {
    return `Campus jobs are limited to ${rules.weeklyHoursLimit} hours a week combined (${rules.internationalWeeklyHoursLimit} for international students).`;
}
